import React, { useEffect, useState } from "react";
import { Table, Form } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { filterByParams } from "../../utils/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CsvDownloader from "react-csv-downloader";
import { CiExport } from "react-icons/ci";
import withAuth from "../../utils/withAuth";
import { numberFormat } from "../../components/numberFormat";

const TopProductsByStateReport = () => {
    const [stateSummary, setStateSummary] = useState([]); // left table
    const [topProductsForState, setTopProductsForState] = useState([]); // right table
    const [selectedState, setSelectedState] = useState("All States");
    const [topLimit, setTopLimit] = useState(5); // Top 5 default
    const [isLoading, setIsLoading] = useState(false);

    const [dateRange, setDateRange] = useState({
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
    });

    const adminid = localStorage.getItem("admindata");
    const navigate = useNavigate();

    // ---------- COMMON FETCH FUNCTION ----------
    const loadReport = async (options = {}) => {
        setIsLoading(true);

        const payload = {
            state: options.state || selectedState || "All States",
            limit: options.limit || topLimit || 5,
            startDate: options.startDate || dateRange.startDate,
            endDate: options.endDate || dateRange.endDate,
        };

        try {
            const res = await filterByParams(
                "super-admin-get-top-product-by-state-report-one-months",
                payload
            );

            if (res.data.status === 1) {
                const data = res.data;
                setStateSummary(data.states || []);
                setTopProductsForState(data.products || []);
                setSelectedState(data.state || payload.state);

                setDateRange({
                    startDate: data.start_date || payload.startDate,
                    endDate: data.end_date || payload.endDate,
                });
            }
        } catch (err) {
            console.error("Error fetching top products by state:", err);
        }

        setIsLoading(false);
    };

    // ---------- INITIAL LOAD ----------
    useEffect(() => {
        if (!adminid) {
            navigate("/");
        } else {
            loadReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminid]);

    // ---------- HANDLERS ----------
    const handleStateChange = async (e) => {
        const newState = e.target.value;
        setSelectedState(newState);
        await loadReport({ state: newState });
    };

    const handleLimitChange = async (e) => {
        const newLimit = Number(e.target.value);
        setTopLimit(newLimit);
        await loadReport({ limit: newLimit });
    };

    const handleDateRangeApply = async (event, picker) => {
        const selectedRange = {
            startDate: picker.startDate.format("YYYY-MM-DD"),
            endDate: picker.endDate.format("YYYY-MM-DD"),
        };
        setDateRange(selectedRange);
        await loadReport(selectedRange);
    };

    // ---------- EXPORT CONFIG ----------
    const stateColumns = [
        { id: "state_name", displayName: "State" },
        { id: "total_orders", displayName: "Orders" },
        { id: "total_revenue", displayName: "Revenue" },
        { id: "top_product_name", displayName: "Top Product" },
        { id: "share_percent", displayName: "% Share" },
    ];

    const stateExportData = stateSummary.map((row) => ({
        state_name: row.state_name || "",
        total_orders: row.total_orders || 0,
        total_revenue: row.total_revenue || 0,
        top_product_name: row.top_product_name || "",
        share_percent: row.share_percent || 0,
    }));

    const productColumns = [
        { id: "p_name", displayName: "Product" },
        { id: "p_sku_number", displayName: "SKU" },
        { id: "total_orders", displayName: "Orders" },
        { id: "total_qty", displayName: "Qty" },
        { id: "total_revenue", displayName: "Revenue" },
        { id: "share_percent", displayName: "% Share" },
        { id: "avg_ticket", displayName: "Avg Ticket" },
    ];

    const productExportData = topProductsForState.map((p) => ({
        p_name: p.p_name || "",
        p_sku_number: p.p_sku_number || "",
        total_orders: p.total_orders || 0,
        total_qty: p.total_qty || 0,
        total_revenue: p.total_revenue || 0,
        share_percent: p.share_percent || 0,
        avg_ticket: p.avg_ticket || 0,
    }));

    const headingState = selectedState || "All States";

    return (
        <div className="top-products-state-report misDashboard">
            <Helmet>
                <title>SATKARTAR: Top Product By State</title>
            </Helmet>

            <ContentWrapper>
                <ReportNavBar />

                {/* Top bar: Date range picker */}
                <div className="d-flex justify-content-end mb-3">
                    <DateRangePicker
                        initialSettings={{
                            startDate: moment(dateRange.startDate, "YYYY-MM-DD"),
                            endDate: moment(dateRange.endDate, "YYYY-MM-DD"),
                        }}
                        onApply={handleDateRangeApply}
                    >
                        <button className="btn btn-sm btndaterange">
                            {moment(dateRange.startDate, "YYYY-MM-DD").format("DD MMM YYYY")} –{" "}
                            {moment(dateRange.endDate, "YYYY-MM-DD").format("DD MMM YYYY")}
                        </button>
                    </DateRangePicker>
                </div>

                <div className="row" style={{ marginBottom: "50px" }}>
                    {/* LEFT: STATES RANKING */}
                    <div className="col-lg-5 mb-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                                <h6
                                    className="mb-0 text-uppercase"
                                    style={{ letterSpacing: "0.05em" }}
                                >
                                    States Ranking
                                </h6>

                                <div className="d-flex align-items-center gap-2">
                                    <CsvDownloader
                                        filename={`STATE_RAKINGS_${moment().format(
                                            "YYYY_MM_DD"
                                        )}`}
                                        extension=".csv"
                                        columns={stateColumns}
                                        datas={stateExportData}
                                    >
                                        <button className="btn btn-xs d-flex align-items-center gap-1" style={{background:"#0dcaf0", color:"#fff"}}>
                                            <CiExport />
                                            <span>Export</span>
                                        </button>
                                    </CsvDownloader>

                                    <Form.Select
                                        size="sm"
                                        style={{ width: 150 }}
                                        onChange={handleStateChange}
                                        value={selectedState}
                                    >
                                        <option value="All States">All States</option>
                                        {stateSummary.map((row) => (
                                            <option
                                                key={row.state_name}
                                                value={row.state_name}
                                            >
                                                {row.state_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>State</th>
                                            <th>Total Orders</th>
                                            <th>Revenue</th>
                                            <th>Top Product</th>
                                            <th>% Share (Qty)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            [...Array(5)].map((_, rowIdx) => (
                                                <tr key={rowIdx}>
                                                    {Array.from({ length: 6 }).map((__, colIdx) => (
                                                        <td key={colIdx}>
                                                            <Skeleton height={16} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : stateSummary.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        ) : (
                                            stateSummary.map((row, idx) => (
                                                <tr
                                                    key={row.state_name || idx}
                                                    onClick={() =>
                                                        loadReport({ state: row.state_name })
                                                    }
                                                    style={{
                                                        cursor: "pointer",
                                                        backgroundColor:
                                                            selectedState === row.state_name
                                                                ? "#f5f7ff"
                                                                : "transparent",
                                                    }}
                                                >
                                                    <td>{idx + 1}</td>
                                                    <td>{row.state_name
                                                        ? row.state_name.length > 10
                                                            ? row.state_name.substring(0, 10).toUpperCase() + "..."
                                                            : row.state_name.toUpperCase()
                                                        : "UNKNOWN"}</td>
                                                    <td>
                                                        {(row.total_orders || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {numberFormat((row.total_revenue || 0).toLocaleString(
                                                            "en-IN"
                                                        ))}
                                                    </td>
                                                    <td>{row.top_product_name || "-"}</td>
                                                    <td>{row.share_percent || 0}%</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: TOP PRODUCTS FOR SELECTED STATE / ALL STATES */}
                    <div className="col-lg-7 mb-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                                <h6
                                    className="mb-0 text-uppercase"
                                    style={{ letterSpacing: "0.05em" }}
                                >
                                    Top Products – {headingState}
                                </h6>

                                <div className="d-flex align-items-center gap-2">
                                    <CsvDownloader
                                        filename={`TOP_PRODUCTS_${headingState.replace(
                                            /\s+/g,
                                            "_"
                                        )}_${moment().format("YYYY_MM_DD")}`}
                                        extension=".csv"
                                        columns={productColumns}
                                        datas={productExportData}
                                    >
                                        <button className="btn btn-xs d-flex align-items-center gap-1" style={{background:"#0dcaf0", color:"#fff"}}>
                                            <CiExport />
                                            <span>Export</span>
                                        </button>
                                    </CsvDownloader>

                                    <Form.Select
                                        size="sm"
                                        style={{ width: 100 }}
                                        value={topLimit}
                                        onChange={handleLimitChange}
                                    >
                                        <option value={5}>Top 5</option>
                                        <option value={10}>Top 10</option>
                                        <option value={20}>Top 20</option>
                                    </Form.Select>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Orders</th>
                                            <th>Qty</th>
                                            <th>Revenue</th>
                                            <th>% Share (Qty)</th>
                                            <th>Avg Ticket</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            [...Array(5)].map((_, rowIdx) => (
                                                <tr key={rowIdx}>
                                                    {Array.from({ length: 8 }).map((__, colIdx) => (
                                                        <td key={colIdx}>
                                                            <Skeleton height={16} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : topProductsForState.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        ) : (
                                            topProductsForState.map((p, index) => (
                                                <tr key={p.p_sku_number || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{p.p_name}</td>
                                                    <td>{p.p_sku_number}</td>
                                                    <td>
                                                        {(p.total_orders || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {(p.total_qty || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        ₹{" "}
                                                        {(p.total_revenue || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {p.share_percent || 0}%
                                                    </td>
                                                    <td>
                                                        ₹{" "}
                                                        {(p.avg_ticket || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(TopProductsByStateReport);
